// ConnectBubblePage — shown post-signin when no active Bubble integration exists.
// Rendered outside AppShell (no sidebar) — full-page centered card.
//
// Single connect flow: paste Bubble API key + app_slug + app_id.
//   POST /api/v1/integrations/bubble/connect { api_key, app_slug, app_id }
//
// IntegrationGate re-evaluates after return and passes through to dashboard.

import { useState } from "react";
import { KeyRound, ExternalLink } from "lucide-react";
import { ApiError } from "@avagency/frontend/lib/api";
import { Button } from "@avagency/frontend/components/ui/button";
import { Footer } from "../components/Footer";
import { connectBubble } from "../api";

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  describedBy?: string;
}

function Field({
  id,
  label,
  hint,
  type = "text",
  value,
  onChange,
  placeholder,
  disabled,
  describedBy,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label
          htmlFor={id}
          className="text-sm font-medium"
          style={{ color: "hsl(220 18% 82%)" }}
        >
          {label}
        </label>
        {hint && (
          <span className="text-xs text-muted-foreground">{hint}</span>
        )}
      </div>
      <input
        id={id}
        type={type}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border px-3 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2"
        style={{
          background: "hsl(222 67% 4% / 0.7)",
          borderColor: "hsl(220 18% 85% / 0.18)",
          color: "hsl(220 18% 88%)",
          fontFamily: type === "password" ? "inherit" : "var(--font-mono, ui-monospace)",
        }}
        disabled={disabled}
        aria-describedby={describedBy}
      />
    </div>
  );
}

export function ConnectBubblePage() {
  const [apiKey, setApiKey] = useState("");
  const [appSlug, setAppSlug] = useState("");
  const [appId, setAppId] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!apiKey.trim() || !appSlug.trim() || !appId.trim()) return;
    setPending(true);
    setError(null);
    try {
      await connectBubble({
        api_key: apiKey.trim(),
        app_slug: appSlug.trim(),
        app_id: appId.trim(),
      });
      // IntegrationGate query will re-evaluate on navigation.
      window.location.replace("/");
    } catch (err) {
      setPending(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to connect Bubble. Please check your credentials.");
      }
    }
  }

  const canSubmit = apiKey.trim() && appSlug.trim() && appId.trim() && !pending;

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-8 p-4">
      <div className="w-full max-w-md space-y-6 animate-fade-up">

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div
              className="flex items-center justify-center rounded-lg"
              style={{
                width: 40,
                height: 40,
                background: "hsl(217 91% 60% / 0.12)",
                border: "1px solid hsl(217 91% 60% / 0.25)",
              }}
              aria-hidden="true"
            >
              <KeyRound className="h-5 w-5" style={{ color: "hsl(217 91% 65%)" }} />
            </div>
          </div>
          <h1
            className="text-xl font-semibold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Connect your Bubble app
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
            Paste your Bubble API credentials to start monitoring Workflow Unit usage.
          </p>
        </div>

        {/* Form card */}
        <div
          className="rounded-xl border p-6 space-y-5"
          style={{
            background:
              "linear-gradient(180deg, hsl(223 55% 10% / 0.8), hsl(222 67% 5% / 0.95))",
            borderColor: "hsl(220 18% 85% / 0.12)",
            boxShadow:
              "0 1px 0 hsl(220 18% 85% / 0.05) inset, 0 20px 40px hsl(222 67% 5% / 0.4)",
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field
              id="bubble-api-key"
              label="API Key"
              hint="Private key"
              type="password"
              value={apiKey}
              onChange={setApiKey}
              placeholder="Your Bubble private API key"
              disabled={pending}
              describedBy={error ? "bubble-error" : undefined}
            />
            <Field
              id="bubble-app-slug"
              label="App Slug"
              hint="From your app URL"
              value={appSlug}
              onChange={setAppSlug}
              placeholder="my-app"
              disabled={pending}
            />
            <Field
              id="bubble-app-id"
              label="App ID"
              hint="UUID"
              value={appId}
              onChange={setAppId}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              disabled={pending}
            />

            {error && (
              <div
                id="bubble-error"
                role="alert"
                className="flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
                style={{
                  background: "hsl(0 100% 75% / 0.06)",
                  borderColor: "hsl(0 100% 75% / 0.22)",
                  color: "hsl(0 100% 78%)",
                }}
              >
                <span className="mt-0.5 flex-shrink-0" aria-hidden="true">⚠</span>
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit}
              aria-busy={pending}
            >
              {pending ? (
                <span className="flex items-center gap-2">
                  <span
                    className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
                    aria-hidden="true"
                  />
                  Connecting…
                </span>
              ) : (
                "Connect Bubble"
              )}
            </Button>
          </form>
        </div>

        {/* Help callout */}
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            background: "hsl(217 91% 60% / 0.05)",
            borderColor: "hsl(217 91% 60% / 0.18)",
          }}
        >
          <p className="text-muted-foreground">
            <span
              className="font-medium"
              style={{ color: "hsl(217 91% 68%)" }}
            >
              Where to find your credentials:
            </span>{" "}
            In your Bubble editor, go to{" "}
            <span className="font-medium text-foreground/80">
              Settings → API → Private key
            </span>
            . Your App ID is in the URL as the UUID after{" "}
            <code
              className="rounded px-1 py-0.5 text-xs"
              style={{
                background: "hsl(217 91% 60% / 0.1)",
                color: "hsl(217 91% 70%)",
              }}
            >
              /app/
            </code>
            .{" "}
            <a
              href="https://bubble.io/documentation"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
              style={{ color: "hsl(217 91% 65%)" }}
            >
              Bubble docs
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </p>
        </div>
      </div>

      <Footer compact />
    </div>
  );
}
